import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  outlineArticlesFromTranscript,
  draftArticleFromTranscript,
} from "@/lib/blog-ai.functions";
import { upsertPost } from "@/lib/blog.functions";

export const Route = createFileRoute("/_authenticated/admin/transcript")({
  component: TranscriptPage,
  head: () => ({ meta: [{ title: "Transcript → Articles - KCN Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
});

type Proposal = {
  title: string;
  slug: string;
  angle: string;
  tags: string[];
  anchors: string[];
  status?: "pending" | "drafting" | "saving" | "done" | "error";
  error?: string;
  postId?: string;
};

const inputCls = "w-full bg-surface border border-border px-4 py-2.5 text-foreground";
const labelCls = "text-xs uppercase tracking-[0.16em] text-foreground/65 mb-1.5 block";

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80);
}

function TranscriptPage() {
  const navigate = useNavigate();
  const outline = useServerFn(outlineArticlesFromTranscript);
  const draft = useServerFn(draftArticleFromTranscript);
  const save = useServerFn(upsertPost);

  const [transcript, setTranscript] = useState("");
  const [count, setCount] = useState(4);
  const [instructions, setInstructions] = useState("");
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [outlining, setOutlining] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function runOutline() {
    setErr(null);
    setOutlining(true);
    setProposals([]);
    try {
      const res = await outline({ data: { transcript, count, instructions } });
      setProposals(res.proposals.map((p) => ({
        ...p,
        slug: slugify(p.slug || p.title),
        tags: p.tags ?? [],
        anchors: p.anchors ?? [],
        status: "pending",
      })));
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setOutlining(false);
    }
  }

  function updateProposal(i: number, patch: Partial<Proposal>) {
    setProposals((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  }

  async function draftAndSave(i: number) {
    const p = proposals[i];
    updateProposal(i, { status: "drafting", error: undefined });
    try {
      const drafted = await draft({
        data: {
          transcript,
          title: p.title,
          slug: slugify(p.slug),
          angle: p.angle,
          tags: p.tags,
          anchors: p.anchors,
        },
      });
      updateProposal(i, { status: "saving" });
      const saved = await save({
        data: {
          slug: slugify(p.slug),
          title: p.title,
          excerpt: drafted.excerpt,
          body_markdown: drafted.body_markdown,
          author_name: "Kristan Cole Network",
          tags: p.tags,
          seo_title: drafted.seo_title,
          seo_description: drafted.seo_description,
          status: "draft",
          key_takeaways: drafted.key_takeaways,
          faqs: drafted.faqs,
        } as any,
      });
      updateProposal(i, { status: "done", postId: (saved as any)?.id });
    } catch (e: any) {
      updateProposal(i, { status: "error", error: e.message });
    }
  }

  async function draftAll() {
    setBulkBusy(true);
    for (let i = 0; i < proposals.length; i++) {
      if (proposals[i].status === "done") continue;
      // sequential to keep rate limits happy
      // eslint-disable-next-line no-await-in-loop
      await draftAndSave(i);
    }
    setBulkBusy(false);
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="headline-md">Transcript → Articles</h1>
        <p className="text-foreground/70 mt-2 max-w-2xl">
          Paste a monthly roundtable transcript. AI will propose {count} MOTM articles grounded in what was actually said, then draft each one and save them as drafts you can review and publish.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-4">
        <div className="lg:col-span-3">
          <label className={labelCls}>Transcript</label>
          <textarea
            rows={16}
            className={`${inputCls} font-mono text-xs`}
            placeholder="Paste the full transcript here…"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
          />
        </div>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>How many articles?</label>
            <input
              type="number"
              min={1}
              max={8}
              className={inputCls}
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(8, Number(e.target.value) || 4)))}
            />
          </div>
          <div>
            <label className={labelCls}>Angle / focus (optional)</label>
            <textarea
              rows={5}
              className={inputCls}
              placeholder="e.g. lean into buyer sentiment stats; skip anything about recruiting"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={runOutline}
            disabled={outlining || transcript.trim().length < 200}
            className="w-full bg-primary px-6 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {outlining ? "Analyzing transcript…" : "Propose articles"}
          </button>
          {err ? <p className="text-primary text-sm">{err}</p> : null}
        </div>
      </div>

      {proposals.length > 0 ? (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="headline-sm">Proposed articles</h2>
            <button
              type="button"
              onClick={draftAll}
              disabled={bulkBusy}
              className="bg-primary px-5 py-2.5 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {bulkBusy ? "Drafting all…" : "Draft & save all"}
            </button>
          </div>

          <div className="space-y-4">
            {proposals.map((p, i) => (
              <div key={i} className="border border-border bg-surface p-4 space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className={labelCls}>Title</label>
                    <input
                      className={inputCls}
                      value={p.title}
                      onChange={(e) => updateProposal(i, { title: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Slug</label>
                    <input
                      className={inputCls}
                      value={p.slug}
                      onChange={(e) => updateProposal(i, { slug: slugify(e.target.value) })}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Angle</label>
                  <textarea
                    rows={2}
                    className={inputCls}
                    value={p.angle}
                    onChange={(e) => updateProposal(i, { angle: e.target.value })}
                  />
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className={labelCls}>Tags (comma separated)</label>
                    <input
                      className={inputCls}
                      value={p.tags.join(", ")}
                      onChange={(e) => updateProposal(i, { tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Anchors (one per line)</label>
                    <textarea
                      rows={3}
                      className={inputCls}
                      value={p.anchors.join("\n")}
                      onChange={(e) => updateProposal(i, { anchors: e.target.value.split("\n").map((t) => t.trim()).filter(Boolean) })}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => draftAndSave(i)}
                    disabled={p.status === "drafting" || p.status === "saving"}
                    className="border border-border px-4 py-2 text-xs uppercase tracking-[0.18em] text-foreground hover:bg-background disabled:opacity-50"
                  >
                    {p.status === "drafting" ? "Drafting…" : p.status === "saving" ? "Saving…" : p.status === "done" ? "Regenerate" : "Draft & save"}
                  </button>
                  {p.status === "done" && p.postId ? (
                    <button
                      type="button"
                      onClick={() => navigate({ to: "/admin/edit/$id", params: { id: p.postId! } })}
                      className="text-xs uppercase tracking-[0.18em] text-primary hover:underline"
                    >
                      Open draft →
                    </button>
                  ) : null}
                  {p.status === "error" ? <span className="text-primary text-sm">{p.error}</span> : null}
                  {p.status === "done" ? <span className="text-xs uppercase tracking-[0.16em] text-foreground/60">Saved as draft</span> : null}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
